/**
 * Valores da gaveta de tarefa e conversão para a API (criar/editar).
 * Puro (sem React) para ser testado à parte.
 */
import type { AgendaRequest, CreateTaskDto, TaskCategory, UpdateTaskDto } from '@/types/tasks'
import type { PriorityLevel, TaskView } from '@/lib/tasks-board'
import type { TaskPrefill } from '@/contexts/TaskDrawerContext'

export interface CaseLink {
  id: string
  title: string
  number: string
  clientId: string
  clientName?: string
}

export interface AgendaValues {
  enabled: boolean
  tipo: AgendaRequest['tipo']
  data: string
  inicio: string
  fim: string
  local: string
}

export interface TaskFormValues {
  title: string
  description: string
  assigned_user_id: string
  prioridade: PriorityLevel
  category: TaskCategory
  prazo_fatal: string
  prazo_interno: string
  carteira_id: string
  attachment_url: string
  caseLink: CaseLink | null
  agenda: AgendaValues
}

export const EMPTY_AGENDA: AgendaValues = { enabled: false, tipo: 'AUDIENCIA', data: '', inicio: '', fim: '', local: '' }

const day = (value?: string | null) => (value ? value.slice(0, 10) : '')

export function valuesFromPrefill(prefill: TaskPrefill | null, currentUserId: string): TaskFormValues {
  const p = prefill ?? {}
  return {
    title: p.title ?? '',
    description: p.description ?? '',
    assigned_user_id: p.assigned_user_id ?? currentUserId,
    prioridade: p.prioridade ?? 'P1',
    category: p.category ?? 'ACAO_INTERNA',
    prazo_fatal: day(p.prazo_fatal),
    prazo_interno: day(p.prazo_interno ?? p.due_date),
    carteira_id: p.carteira_id ?? '',
    attachment_url: p.attachment_url ?? '',
    caseLink: p.case_id && p.client_id
      ? { id: p.case_id, title: p.case_title ?? '', number: p.case_number ?? '', clientId: p.client_id, clientName: p.client_name }
      : null,
    agenda: EMPTY_AGENDA,
  }
}

export function valuesFromTask(task: TaskView): TaskFormValues {
  return {
    title: task.title,
    description: task.description ?? '',
    assigned_user_id: task.assigned_user_id,
    prioridade: task.prioridade,
    category: task.category,
    prazo_fatal: day(task.prazo_fatal),
    prazo_interno: day(task.prazo_interno ?? task.due_date),
    carteira_id: task.carteira_id ?? '',
    attachment_url: task.attachment_url ?? '',
    caseLink: task.case_id && task.client_id
      ? { id: task.case_id, title: task.case_title ?? '', number: task.case_number ?? '', clientId: task.client_id, clientName: task.client_name }
      : null,
    agenda: EMPTY_AGENDA,
  }
}

/** Mensagens de validação por campo (vazio = válido). */
export function validateTask(values: TaskFormValues): Record<string, string> {
  const errors: Record<string, string> = {}
  if (values.title.trim().length < 3) errors.title = 'Descreva a tarefa começando por um verbo (mínimo 3 caracteres)'
  if (!values.prazo_interno && !values.prazo_fatal) errors.prazo_interno = 'Informe o prazo'
  if (values.prazo_fatal && values.prazo_interno && values.prazo_interno > values.prazo_fatal) {
    errors.prazo_interno = 'Prazo interno não pode ser depois do fatal'
  }
  if (values.attachment_url && !/^https?:\/\//i.test(values.attachment_url)) errors.attachment_url = 'Anexo deve ser um link (http/https)'
  if (values.agenda.enabled) {
    if (!values.agenda.data) errors.agenda = 'Informe a data do compromisso'
    else if (values.agenda.fim && (!values.agenda.inicio || values.agenda.fim <= values.agenda.inicio)) errors.agenda = 'Fim deve ser depois do início'
  }
  return errors
}

function agendaOf(values: TaskFormValues): AgendaRequest | undefined {
  if (!values.agenda.enabled) return undefined
  return {
    tipo: values.agenda.tipo,
    data: values.agenda.data,
    inicio: values.agenda.inicio || null,
    fim: values.agenda.fim || null,
    local: values.agenda.local.trim() || null,
  }
}

export function toCreatePayload(values: TaskFormValues, prefill: TaskPrefill | null): CreateTaskDto {
  const link = values.caseLink
  const payload: CreateTaskDto = {
    title: values.title.trim(),
    description: values.description.trim() || undefined,
    prioridade: values.prioridade,
    category: values.category,
    assigned_user_id: values.assigned_user_id || undefined,
    prazo_fatal: values.prazo_fatal || null,
    prazo_interno: values.prazo_interno || null,
    carteira_id: values.carteira_id || null,
    attachment_url: values.attachment_url.trim() || null,
    publication_id: prefill?.publication_id,
    origin_type: prefill?.origin_type,
    origin_id: prefill?.origin_id,
    interaction_id: prefill?.interaction_id,
    client_id: link?.clientId ?? prefill?.client_id,
    client_name: link?.clientName ?? prefill?.client_name,
    tags: prefill?.tags,
    agenda: agendaOf(values),
  }
  if (link) Object.assign(payload, { case_id: link.id, case_title: link.title || link.number, case_number: link.number })
  return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined)) as CreateTaskDto
}

/** Só os campos que mudaram (a API recusa PUT sem campo). */
export function toUpdatePayload(values: TaskFormValues, task: TaskView): UpdateTaskDto {
  const original = valuesFromTask(task)
  const patch: UpdateTaskDto = {}
  if (values.title.trim() !== original.title) patch.title = values.title.trim()
  if (values.description.trim() !== original.description) patch.description = values.description.trim()
  if (values.assigned_user_id !== original.assigned_user_id) patch.assigned_user_id = values.assigned_user_id
  if (values.prioridade !== original.prioridade) patch.prioridade = values.prioridade
  if (values.category !== original.category) patch.category = values.category
  if (values.prazo_fatal !== original.prazo_fatal) patch.prazo_fatal = values.prazo_fatal || null
  if (values.prazo_interno !== original.prazo_interno) patch.prazo_interno = values.prazo_interno || null
  if (values.carteira_id !== original.carteira_id) patch.carteira_id = values.carteira_id || null
  if (values.attachment_url.trim() !== original.attachment_url) patch.attachment_url = values.attachment_url.trim() || null
  return patch
}

export { agendaOf }
